import { evaluate } from './formula.js'

/**
 * The offer document as Core lays it out (RevisionService::resolve/number, OfferTotals,
 * DocxWriter::columnWidths, the document view), done in the browser so the editor shows the
 * pages while they are typed, offline too (D-150). Keep in step with Core.
 */

/** A summary's line that prints (D-191, as Core's RevisionService::summaryLines): one with an amount. */
export const summaryLineShown = (l) => !l.hidden && (!!l.text || (l.manual ? l.value !== null && l.value !== undefined : Number(l.value) !== 0))

export const MM = 96 / 25.4 // CSS pixels in a millimetre

export const tr = (v, lang) => (v && typeof v === 'object' ? v[lang] ?? v.pl ?? v.en ?? Object.values(v)[0] ?? '' : v ?? '')

const isNum = (v) => v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v))

function group(intPart, sep) {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, sep)
}
/** 32 070,00 (pl) / 32,070.00 (en), as PHP's number_format does in the document. */
export function money(v, lang = 'pl') {
  if (!isNum(v)) return ''
  const n = Number(v)
  const [i, d] = Math.abs(n).toFixed(2).split('.')
  return (n < 0 && Number(Math.abs(n).toFixed(2)) !== 0 ? '-' : '') + group(i, lang === 'pl' ? ' ' : ',') + (lang === 'pl' ? ',' : '.') + d
}
function trimmed(v, digits) {
  const n = Number(v)
  const [i, d] = Math.abs(n).toFixed(digits).split('.')
  const dec = (d || '').replace(/0+$/, '')
  return (n < 0 ? '-' : '') + group(i, ' ') + (dec ? ',' + dec : '')
}
const PERIODS = { monthly: ['miesięcznie', 'monthly'], yearly: ['rocznie', 'yearly'], quarterly: ['kwartalnie', 'quarterly'], once: ['jednorazowo', 'one-off'] }

/** A cell's text as the document prints it. */
export function cellText(v, column, lang = 'pl') {
  switch (column?.type || 'text') {
    case 'money': return money(v, lang)
    case 'percent': return isNum(v) ? trimmed(v, 2) + ' %' : ''
    case 'bool': return v ? '✓' : '—'
    case 'number': return isNum(v) ? trimmed(v, 3) : ''
    case 'period': return PERIODS[v] ? PERIODS[v][lang === 'pl' ? 0 : 1] : String(v ?? '')
    default: return String(v ?? '')
  }
}
export const numeric = (c) => ['number', 'money', 'percent'].includes(c?.type || 'text')

/** Column widths in millimetres (DocxWriter::columnWidths): the "No." column first when shown. */
export function columnWidths(columns, index, total = 160, indexWidth = 12) {
  const widths = []
  let fixed = index ? indexWidth : 0
  const flex = []
  columns.forEach((c, i) => {
    widths[i] = c.width ? Number(c.width) : ({ money: 28, number: 20, percent: 20, period: 24, bool: 16 }[c.type] ?? 0)
    if (widths[i] === 0) flex.push(i)
    else fixed += widths[i]
  })
  const rest = Math.max(30 * flex.length, total - fixed)
  flex.forEach((i) => { widths[i] = Math.round((rest / Math.max(1, flex.length)) * 10) / 10 })
  return [...(index ? [indexWidth] : []), ...widths]
}

/** A row's values with its quantity and computed columns (OfferTotals::rowValues). */
export function rowValues(row, columns, added = null) {
  const values = { ...(row.values || {}) }
  // The equipment priced in this row (D-156).
  for (const [key, amount] of Object.entries(added || {})) values[key] = Math.round((Number(values[key] || 0) + amount + Number.EPSILON) * 100) / 100
  if (row.quantity !== null && row.quantity !== undefined) values.quantity = Number(row.quantity)
  for (const c of columns || []) {
    if (c.formula) {
      const r = evaluate(c.formula, values)
      values[c.key] = typeof r === 'number' ? Math.round((r + Number.EPSILON) * 100) / 100 : r
    }
  }
  return values
}
/** A row's texts in place of amounts ({column: text}), the empty ones left out. */
export function rowTexts(row) {
  return Object.fromEntries(Object.entries(row?.meta?.texts || {}).filter(([, t]) => typeof t === 'string' && t.trim() !== ''))
}
/** Whether a row counts nothing: hidden, optional, of quantity 0, "to be agreed", or with a text in place of an amount. */
// Words in its quantity are not in place of an amount: it still counts (Core's OfferTotals::texted, D-271).
export const countsNothing = (row, values) => row.hidden || row.is_optional || Number(values.quantity ?? 1) === 0 || !!row.meta?.tbd || Object.keys(rowTexts(row)).some((k) => k !== 'quantity') || inBase(row)

/** Equipment priced in its base's row (D-156): not a row of its own. */
export const inBase = (row) => Boolean(row?.meta?.accessory_of) && !row.meta.separate

/** What a table's base rows add for the equipment priced in them (Core's OfferTotals::equipmentPrices). */
export function equipmentPrices(rows, columns) {
  const priced = (columns || []).filter((c) => c.type === 'money' && !c.formula).map((c) => c.key)
  const out = {}
  for (const row of rows || []) {
    if (!inBase(row) || row.hidden) continue
    const base = row.meta.accessory_of
    const perUnit = Number(row.meta.per_unit ?? 1)
    for (const key of priced) {
      const v = row.values?.[key]
      if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) continue
      out[base] = out[base] || {}
      out[base][key] = Math.round(((out[base][key] || 0) + Number(v) * perUnit + Number.EPSILON) * 100) / 100
    }
  }
  return out
}

/** The lines priced-in equipment adds to its base row's name (Core's OfferTotals::equipmentNames). {base uuid: {group: [line…]}} */
export function equipmentNames(rows, lang) {
  const out = {}
  for (const row of rows || []) {
    if (!inBase(row) || row.hidden) continue
    // The offer's own words for the piece, else the catalogue's (Core's OfferTotals, D-271).
    const l = row.meta?.row_line || row.row_line
    const text = String((l && typeof l === 'object' ? l[lang] ?? l.pl ?? Object.values(l)[0] : l) || '').trim()
    if (!text) continue
    const base = (out[row.meta.accessory_of] = out[row.meta.accessory_of] || {})
    const slot = String(row.meta.slot || '')
    ;(base[slot] = base[slot] || []).push(text)
  }
  return out
}

/** A base row's name with its equipment's lines (Core's OfferTotals::nameWithEquipment): in their fields, else at its end. */
export function nameWithEquipment(name, slots, lang = 'pl') {
  if ((!slots || !Object.keys(slots).length) && !/\{\{\s*component\.equipment/i.test(name || '')) return name ?? ''
  slots = slots || {}
  const rest = { ...slots }
  const out = []
  for (const line of String(name ?? '').split(/\r?\n/)) {
    const m = line.trim().match(/^\{\{\s*component\.equipment(?:\.([a-z0-9_]+))?\s*\}\}$/i)
    if (m) {
      const slot = (m[1] || '').toLowerCase()
      out.push(...(rest[slot] || []))
      delete rest[slot]
    } else {
      // Within a sentence: the pieces as a list, "A, B oraz C".
      // A field that brings nothing takes the comma before it along.
      out.push(line.replace(/(,\s*)?\{\{\s*component\.equipment(?:\.([a-z0-9_]+))?\s*\}\}/gi, (_, comma, s) => {
        const items = [...(rest[(s || '').toLowerCase()] || [])]
        delete rest[(s || '').toLowerCase()]
        if (!items.length) return ''
        const last = items.pop()
        return (comma || '') + (items.length ? `${items.join(', ')}${lang === 'en' ? ' and ' : ' oraz '}${last}` : last)
      }))
    }
  }
  for (const lines of Object.values(rest)) out.push(...lines)
  return out.join('\n').trimEnd()
}

/**
 * The equipment the offer has, per component it equips and per place in its specification
 * (Core's RevisionService::equipmentLines): the pieces' lines, hidden ones left out.
 * {component uuid: {place ('' or a group's key): [line…]}}
 */
export function equipmentLines(blocks, lang) {
  const components = {}
  const pieces = []
  const walk = (nodes, hidden) => {
    for (const n of nodes || []) {
      const off = hidden || !!n.hidden
      if (n.kind === 'row') {
        components[n.uuid] = n.origin?.component?.uuid || null
        if (!off && n.meta?.accessory_of) pieces.push(n)
      }
      walk(n.children, off)
    }
  }
  walk(blocks, false)
  const out = {}
  for (const p of pieces) {
    const base = components[p.meta.accessory_of]
    const line = p.spec_line && typeof p.spec_line === 'object' ? p.spec_line[lang] ?? p.spec_line.pl ?? Object.values(p.spec_line)[0] : p.spec_line
    if (!base || !String(line || '').trim()) continue
    const slot = String(p.meta.slot || '')
    out[base] = out[base] || {}
    out[base][slot] = out[base][slot] || []
    if (!out[base][slot].includes(line)) out[base][slot].push(line)
  }
  return out
}

const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100

/**
 * A summary's lines as Core lists them (D-152): its type's, with the offer's own — a manual
 * amount under its own label — each after the line it names, or at the end.
 */
export function summaryLines(typeLines, extra) {
  const lines = [...(typeLines || [])]
  for (const own of extra || []) {
    if (!own?.key) continue
    const at = lines.findIndex((l) => l.key === own.after)
    lines.splice(at < 0 ? lines.length : at + 1, 0, { key: own.key, label: own.label || {}, manual: true, own: true })
  }
  return lines
}

/**
 * The totals as Core computes them: each table's sums, the sums per table type, and the
 * summaries from their definitions (manual lines from the summary's own values).
 */
export function computeTotals(blocks, summaryTypes = {}) {
  const tables = {}
  const types = {}
  const summaries = {}
  const summaryBlocks = []
  // What is not counted but offered (D-187): options per table type, and the variants to choose
  // instead of the one counted (an SLA's, D-186) — each with the row counted in its place.
  const optional = {}
  const variants = []
  const counted = {}
  const walk = (nodes, hidden) => {
    for (const b of nodes || []) {
      const h = hidden || !!b.hidden
      if (b.kind === 'table' && !h) {
        const columns = b.meta?.columns || []
        const sums = {}
        const rows = (b.children || []).filter((r) => r.kind === 'row')
        const added = equipmentPrices(rows, columns)
        for (const row of rows) {
          const values = rowValues(row, columns, added[row.uuid])
          const key = b.meta?.table_type || ''
          const summed = Object.fromEntries(columns.filter((c) => c.summed && isNum(values[c.key])).map((c) => [c.key, Number(values[c.key])]))
          const component = row.origin?.component?.uuid
          if (row.meta?.variant && component && !row.hidden) {
            if (row.is_optional) variants.push({ uuid: row.uuid, name: row.values?.name || '', variant: row.meta.variant, component, type: key, summed })
            else counted[component] = { variant: row.meta.variant, type: key, summed }
          }
          if (row.is_optional && !row.meta?.variant && !countsNothing({ ...row, is_optional: false }, values)) {
            optional[key] = optional[key] || {}
            for (const [col, v] of Object.entries(summed)) optional[key][col] = round2((optional[key][col] || 0) + v)
          }
          if (countsNothing(row, values)) continue
          for (const c of columns) {
            if (c.summed && isNum(values[c.key])) sums[c.key] = round2((sums[c.key] || 0) + Number(values[c.key]))
          }
        }
        tables[b.uuid] = sums
        const key = b.meta?.table_type || ''
        types[key] = types[key] || {}
        // A table apart (a later stage, an option priced aside): its own total, nothing to the summaries (D-271).
        if (!b.meta?.apart) for (const [col, v] of Object.entries(sums)) types[key][col] = round2((types[key][col] || 0) + v)
      }
      if (b.kind === 'summary') summaryBlocks.push(b)
      walk(b.children, h)
    }
  }
  walk(blocks, false)
  let grand = 0
  // Only the first summary of a kind is the offer's value; a further one (a variant's, a stage's) is not (Core's OfferTotals, D-271).
  const seenKinds = new Set()
  // The tables each summary adds up, for the offer's value (Core's OfferTotals, D-307).
  const sourcesOf = {}
  for (const b of summaryBlocks) {
    const type = summaryTypes[b.source?.key] || null
    if (!type) continue
    const lines = []
    let total = 0
    const sources = []
    let extra = 0
    const alternatives = {}
    // A hidden line stays in the list (the editor shows where it is) but prints and counts nothing (D-152).
    const hidden = new Set(b.meta?.hidden_lines || [])
    // A line named otherwise in this offer or its template (Core's OfferTotals, D-271).
    const named = (line) => {
      const own = Object.fromEntries(Object.entries(b.meta?.labels?.[line.key] || {}).filter(([, v]) => typeof v === 'string' && v.trim() !== ''))
      return Object.keys(own).length ? { ...line, label: { ...(line.label || {}), ...own } } : line
    }
    // The offer's own set of lines, if it has one (D-271).
    const own = Array.isArray(b.meta?.lines) && b.meta.lines.length ? b.meta.lines : type.lines || []
    for (const line of summaryLines(own, b.meta?.extra_lines || []).map(named)) {
      const own = Boolean(line.own)
      const off = hidden.has(line.key)
      if (line.manual) {
        const given = b.values?.[line.key]
        if (given === null || given === undefined || given === '') {
          lines.push({ key: line.key, label: line.label, value: null, manual: true, own, hidden: off })
          continue
        }
        lines.push({ key: line.key, label: line.label, value: round2(Number(given)), manual: true, own, hidden: off })
        if (!off) total += Number(given)
        continue
      }
      let value = 0
      for (const s of line.sources || []) value += Number((s.table ? tables[s.table] : types[s.table_type || ''])?.[s.column || 'total'] || 0)
      if (!off) for (const s of line.sources || []) sources.push(s.table ? `table:${s.table}` : `type:${s.table_type || ''}`)
      lines.push({ key: line.key, label: line.label, value: round2(value), manual: false, own, hidden: off })
      if (!off) total += value
      if (off) continue
      for (const s of line.sources || []) {
        extra += Number(optional[s.table_type || '']?.[s.column || 'total'] || 0)
        for (const v of variants) {
          if (v.type !== (s.table_type || '')) continue
          const col = s.column || 'total'
          alternatives[v.uuid] = (alternatives[v.uuid] || 0) + Number(v.summed[col] || 0) - (counted[v.component]?.type === v.type ? Number(counted[v.component].summed[col] || 0) : 0)
        }
      }
    }
    // Words in place of a line's amount ("Do uzgodnienia"): printed so, counted out (Core's OfferTotals, D-271).
    for (const l of lines) {
      const text = b.meta?.texts?.[l.key]
      const words = String((text && typeof text === 'object' ? text.pl ?? Object.values(text)[0] : text) || '').trim()
      if (!words) continue
      if (!l.hidden) total -= Number(l.value || 0)
      l.text = words
    }
    const grandTotal = type.grand_total ?? true
    for (const l of lines) if (typeof b.meta?.marks?.[l.key] === 'string' && b.meta.marks[l.key]) l.mark = b.meta.marks[l.key]
    const summary = { key: b.source?.key, name: type.name, lines, total_mark: typeof b.meta?.total_mark === 'string' ? b.meta.total_mark : null, unit_label: type.unit_label ?? null, column_label: type.column_label ?? null, total_label: type.total_label ?? null, total: grandTotal ? round2(total) : null, in_grand_total: (type.in_grand_total ?? true) && grandTotal && !seenKinds.has(b.source?.key), repeats: false,
      optional: round2(extra), variants: variants.filter((v) => v.uuid in alternatives).map((v) => ({ uuid: v.uuid, name: v.name, variant: v.variant, instead: counted[v.component]?.variant || null, delta: round2(alternatives[v.uuid]) })) }
    summaries[b.uuid] = summary
    seenKinds.add(b.source?.key)
    sourcesOf[b.uuid] = [...new Set(sources)]
  }
  // A table counts once in the offer's value: a summary adding up tables another one counted stays out
  // whole, whichever stands first — first those of no recurring table (one a summary of monthly costs
  // adds up), then those of fewer tables.
  const recurring = new Set(Object.values(summaryTypes).filter((t) => t?.in_grand_total === false).flatMap((t) => (t.lines || []).flatMap((l) => (l.sources || []).map((s) => `type:${s.table_type || ''}`))))
  const rank = (u) => [sourcesOf[u].filter((s) => recurring.has(s)).length, sourcesOf[u].length]
  const countedTables = new Set()
  for (const uuid of Object.keys(summaries).filter((u) => summaries[u].in_grand_total).sort((a, b) => { const [x, y] = [rank(a), rank(b)]; return x[0] - y[0] || x[1] - y[1] })) {
    if (sourcesOf[uuid].some((s) => countedTables.has(s))) {
      summaries[uuid].in_grand_total = false
      summaries[uuid].repeats = true
      continue
    }
    for (const s of sourcesOf[uuid]) countedTables.add(s)
    grand += summaries[uuid].total || 0
  }
  return { tables, table_types: types, summaries, grand_total: round2(grand) }
}

/** A rich text as it reads: the editor's empty attributes, the order of keys and empty paragraphs at its end left out (Core's RichDoc). */
export function normalizeDoc(node) {
  if (Array.isArray(node)) return node.map(normalizeDoc)
  if (!node || typeof node !== 'object') return node
  const out = {}
  for (const key of Object.keys(node).sort()) {
    let value = node[key]
    if (key === 'attrs' && value && typeof value === 'object') {
      value = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== null && v !== undefined))
      if (!Object.keys(value).length) continue
    }
    out[key] = normalizeDoc(value)
  }
  if (out.type === 'doc' && Array.isArray(out.content)) {
    while (out.content.length && out.content.at(-1)?.type === 'paragraph' && !out.content.at(-1).content?.length) out.content.pop()
  }
  return out
}
export const sameDoc = (a, b) => JSON.stringify(normalizeDoc(a ?? null)) === JSON.stringify(normalizeDoc(b ?? null))

/** A picture's address as this panel reaches it: Core's own addresses relative, whatever host they were kept with. */
export function localSrc(src) {
  return typeof src === 'string' ? src.replace(/^https?:\/\/[^/]+(\/api\/v1\/(?:catalog\/images|attachments)\/)/, '$1') : src
}

/** The terms printed as "Label: text" lines, in this order (Core's TermsLayout::EXTRAS). */
export const TERMS_EXTRAS = ['vat_note', 'delivery_place', 'transport', 'acceptance', 'invoicing', 'bank_account', 'warranty_terms', 'exchange_rate', 'title_retention']

/**
 * The payment terms block as Core's TermsLayout lays it out: one row of advance, term and form —
 * or a table of the instalments when there are several — and the other terms under it.
 * `label(key, values)` gives the document's labels.
 */
export function termsLayout(terms, label, lang) {
  const t = (v) => tr(v, lang)
  const given = (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && !v.length)
  const percent = (v) => String(Math.round(Number(v) * 100) / 100).replace('.', ',')
  const schedule = (terms?.schedule || []).filter((s) => s && (given(s.percent) || String(t(s.trigger) || '').trim() !== ''))
  const form = given(terms?.payment_form) && String(t(terms.payment_form)).trim() ? label('payment_form', { text: t(terms.payment_form) }) : null
  const out = { cells: [], schedule: [], form: null, extras: [] }
  if (schedule.length > 1) {
    out.schedule = schedule.map((s) => [String(t(s.trigger) || '').trim(), given(s.percent) ? `${percent(s.percent)}%` : '', given(s.days) ? label('days', { n: Number(s.days) }) : ''])
    out.form = form
  } else {
    const advance = schedule[0]?.percent ?? terms?.advance_percent
    out.cells = [given(advance) ? label('advance', { n: percent(advance) }) : null, given(terms?.payment_days) ? label('payment_days', { n: terms.payment_days }) : null, form].filter(Boolean)
  }
  for (const key of TERMS_EXTRAS) {
    const text = String(t(terms?.[key]) || '').trim()
    if (text) out.extras.push([label(`terms_${key}`), text])
  }
  return out
}

/** {{key}} filled from the values; a key not known stays as it is. */
export function fill(text, values) {
  return String(text ?? '').replace(/\{\{\s*([a-z_.]+)\s*\}\}/gi, (m, key) => (Object.prototype.hasOwnProperty.call(values || {}, key) ? String(values[key] ?? '') : m))
}

/** A template group's key, which its headings and words name in `with` (OfferTemplateVersion::groupKey, D-359). */
export const groupKey = (g) => (g && typeof g.by === 'string' && g.by ? `${g.by}:${typeof g[g.by] === 'string' ? g[g.by] : ''}` : null)

/** Whether a section or text has anything to print (the document leaves empty tables and groups out). */
// Hidden, or stood in for by a joint item's section (D-302): not printed.
const shown = (b) => !b.hidden && !b.replaced_by

/**
 * What the document prints and how it is numbered (RevisionService::resolve and ::number):
 * hidden blocks out, tables without rows and groups with nothing in them out; chapters (level 1
 * headings, top-level group titles, sections of a group that makes chapters), tables and
 * summaries, figures (also the titled pictures inside sections, counted in order).
 * Returns { printed: Set<uuid>, numbers: {uuid: n}, chapters: Set<uuid> (sections as chapters),
 * levels: {uuid: level of a group title}, figuresBefore: {uuid of section: count},
 * continued: Set<uuid> (sections with the title of the one before them in their group: no heading of
 * their own in print, they go on under it — D-302) }.
 */
export function numbering(blocks, lang, totals) {
  const printed = new Set()
  const numbers = {}
  const chapters = new Set()
  const continued = new Set()
  const levels = {}
  const figuresBefore = {}
  const counters = { chapter: 0, table: 0, figure: 0 }

  // first: what prints
  const keep = (b) => {
    if (!shown(b)) return false
    const kids = level(b.children)
    if ((b.kind === 'table' || b.kind === 'group') && kids.length === 0) return false
    printed.add(b.uuid)
    return true
  }
  // A template's heading or words printed together with its groups (D-359): out when none of them is.
  const level = (nodes) => {
    const kept = (nodes || []).filter(keep)
    const groups = new Set(kept.map((b) => (b.kind === 'group' ? groupKey(b.meta?.structure) : null)).filter(Boolean))
    return kept.filter((b) => {
      if (!Array.isArray(b.meta?.with) || !b.meta.with.length || b.meta.with.some((k) => groups.has(k))) return true
      printed.delete(b.uuid)
      return false
    })
  }
  level(blocks)

  const textOf = (b) => {
    const c = b.content
    const v = c && typeof c === 'object' ? c[lang] : c
    return typeof v === 'string' ? v : ''
  }
  const titleOf = (b) => (b.meta?.title && typeof b.meta.title === 'object' && lang in b.meta.title ? String(b.meta.title[lang] ?? '').trim() : tr(b.source?.title, lang) || '')
  const countImages = (doc) => {
    let n = 0
    const walk = (node) => {
      if (!node || typeof node !== 'object') return
      if (node.type === 'image' && node.attrs?.title) n++
      for (const c of node.content || []) walk(c)
    }
    walk(doc)
    return n
  }
  const contentDoc = (b) => {
    const own = b.content && typeof b.content === 'object' ? b.content[lang] : null
    return own ?? (b.source_content ? b.source_content[lang] : null)
  }

  const walk = (nodes, depth, chaptersHere) => {
    let previous = null
    for (const b of nodes || []) {
      if (!printed.has(b.uuid)) continue
      if (b.kind === 'section' || b.kind === 'text') {
        const title = titleOf(b).trim().toLowerCase()
        if (title && title === previous) continued.add(b.uuid)
        else if (title) previous = title
      } else previous = null
      if (b.kind === 'heading' && Number(b.meta?.level ?? 1) === 1 && textOf(b).trim()) numbers[b.uuid] = ++counters.chapter
      else if (b.kind === 'group' && textOf(b).trim()) {
        levels[b.uuid] = depth === 0 ? 1 : 2
        if (depth === 0) numbers[b.uuid] = ++counters.chapter
      } else if (b.kind === 'table' || (b.kind === 'summary' && totals?.summaries?.[b.uuid]?.lines?.some(summaryLineShown))) numbers[b.uuid] = ++counters.table
      else if (b.kind === 'figure') numbers[b.uuid] = ++counters.figure
      else if (b.kind === 'section' || b.kind === 'text') {
        if (chaptersHere && b.kind === 'section' && titleOf(b).trim() && !continued.has(b.uuid)) {
          chapters.add(b.uuid)
          numbers[b.uuid] = ++counters.chapter
        }
        figuresBefore[b.uuid] = counters.figure
        counters.figure += countImages(contentDoc(b))
      }
      walk(b.children, depth + 1, b.kind === 'group' && !!b.meta?.structure?.chapters)
    }
  }
  walk(blocks, 0, false)
  return { printed, numbers, chapters, continued, levels, figuresBefore }
}

/**
 * How a block stands against what the offer's building made of its template and catalogue
 * (D-163), for the mark by the page's edge: 'added' in the offer, 'changed' from what was
 * made, or null. A price computed by the configuration — a sum of contributions, an SLA from
 * its calculator — is no change while it stands as computed.
 */
export function editMark(b) {
  if (!b || b.hidden) return null
  if (b.added) return 'added'
  if (!b.customized) return null
  if (b.kind === 'row' && b.pricing) {
    const value = Number(b.values?.[b.pricing.column || 'unit_price'] ?? 0)
    const computed = b.pricing.mode === 'sum' ? b.meta?.sum : b.meta?.sla_total
    if (computed !== undefined && computed !== null && Math.abs(value - Number(computed)) < 0.005) return null
  }
  return 'changed'
}
